import { useMutation } from '@apollo/client'
import Button from '@mui/material/Button'
import CssBaseline from '@mui/material/CssBaseline'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { useRouter } from 'found'
import { useSnackbar } from 'notistack'
import React, { MouseEvent, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { makeStyles } from 'tss-react/mui'
import LinkBody2 from '../../components/navigation/LinkBody2'
import { RequestPasswordResetMutation, RequestPasswordResetMutationVariables } from '../../generated/graphql'
import routes from '../../helpers/routes'
import log from '../../log'
import Profiles from '../../schema/Profiles'

const rnd = Math.floor(Math.random() * 10) + 1 //! The 7 is the number of background images we have

const useStyles = makeStyles()((theme) => {
  return {
    root: {
      height: '100vh',
    },
    image: {
      backgroundImage: `${'url(/media/images/screens/' + rnd + '.jpg)'}`,
      backgroundRepeat: 'no-repeat',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
    },
    paper: {
      margin: theme.spacing(8, 4),
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
    },
    form: {
      width: '100%', // Fix IE 11 issue.
      marginTop: theme.spacing(1),
    },
    submit: {
      margin: theme.spacing(3, 0, 2),
    },
    error: {
      backgroundColor: theme.palette.error.dark,
    },
    body: {
      margin: theme.spacing(2, 0, 1),
    },
  }
})

export default function ForgottenPasswordScreen() {
  const [requestPasswordReset] = useMutation<RequestPasswordResetMutation, RequestPasswordResetMutationVariables>(
    Profiles.REQUEST_PASSWORD_RESET,
  )
  const { t } = useTranslation()
  const { router } = useRouter()
  const { classes } = useStyles()
  const [email, setEmail] = useState('')
  const { enqueueSnackbar } = useSnackbar()

  const submit = async function (e: MouseEvent) {
    e.preventDefault()
    try {
      const variables = {
        input: email,
      }
      await requestPasswordReset({ variables })
      enqueueSnackbar(t('Recovery email sent'), { variant: 'success' })
      router.push(routes.login)
    } catch (ex) {
      log.error(ex)
      enqueueSnackbar(t(ex.graphQLErrors[0]?.message), { variant: 'error' })
    }
  }

  return (
    <Grid container component="main" className={classes.root}>
      <CssBaseline />
      <Grid item xs={false} sm={4} md={7} className={classes.image} />
      <Grid item xs={12} sm={8} md={5} component={Paper} elevation={6} square>
        <div className={classes.paper}>
          <Typography component="h1" variant="h5">
            {t('Forgotten Password')}
          </Typography>

          <Typography className={classes.body} variant="body2">
            {t('Use this form to reset your password or send a new email validation link')}
          </Typography>

          <form className={classes.form} noValidate>
            {/* https://github.com/mui-org/material-ui/issues/14427 */}
            <TextField
              autoFocus
              variant="outlined"
              margin="normal"
              required
              fullWidth
              id="email"
              label={t('Email Address')}
              name="email"
              autoComplete="email"
              type="email"
              onChange={(e) => setEmail(e.target.value)}
            />
            <Button
              variant="contained"
              type="submit"
              fullWidth
              color="primary"
              className={classes.submit}
              onClick={submit}
              disabled={!email}
            >
              {t('Recover Password')}
            </Button>
            <Grid container>
              <Grid item xs>
                <LinkBody2 to={routes.login}>{t('Know your password?')}</LinkBody2>
              </Grid>
            </Grid>
          </form>
        </div>
      </Grid>
    </Grid>
  )
}
